import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  listGoogleCalendars,
  setCalendarEnabled,
  syncGoogleCalendar,
} from "@/lib/calendar.functions";

/**
 * Which calendars Pyng reads. Holiday/birthday-style calendars default off,
 * everything else defaults on — flipping one resyncs right away so Today
 * reflects the choice without a second step.
 */
export function CalendarPicker({ onSynced }: { onSynced?: () => void }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listGoogleCalendars);
  const setFn = useServerFn(setCalendarEnabled);
  const syncFn = useServerFn(syncGoogleCalendar);

  const { data, isLoading } = useQuery({
    queryKey: ["google-calendar-list"],
    queryFn: () => listFn(),
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: async (v: { calendarId: string; calendarName: string; enabled: boolean }) => {
      await setFn({ data: v });
      return syncFn({ data: {} });
    },
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["google-calendar-list"] });
      const prev = qc.getQueryData<any>(["google-calendar-list"]);
      qc.setQueryData<any>(["google-calendar-list"], (old: any) =>
        old
          ? {
              ...old,
              calendars: old.calendars.map((c: any) =>
                c.calendarId === v.calendarId ? { ...c, enabled: v.enabled } : c,
              ),
            }
          : old,
      );
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["google-calendar-list"], ctx.prev);
      toast.error(e?.message ?? "Couldn't update calendars");
    },
    onSuccess: (_r, v) => {
      toast.success(v.enabled ? `${v.calendarName} added` : `${v.calendarName} removed`);
      for (const key of [
        ["google-calendar-list"],
        ["calendar-status"],
        ["upcoming-meetings"],
        ["post-meeting-recaps"],
        ["upcoming-events"],
        ["pending-hooks"],
        ["home-digest"],
        ["sync-history"],
      ])
        qc.invalidateQueries({ queryKey: key });
      onSynced?.();
    },
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-[12px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading your calendars…
      </p>
    );
  }
  const calendars = (data as any)?.calendars ?? [];
  if (calendars.length === 0) {
    return <p className="text-[12px] text-muted-foreground">No calendars on this account.</p>;
  }

  return (
    <div className="space-y-1">
      <p className="text-[12px] text-muted-foreground">
        Choose which calendars Pyng reads. Turning one off removes its meetings.
      </p>
      <ul className="mt-1 divide-y divide-white/5">
        {calendars.map((c: any) => (
          <li key={c.calendarId} className="flex min-h-12 items-center gap-3 py-2.5">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-foreground">{c.name}</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {c.primary ? "Primary" : c.autoSkipped ? "Holidays / birthdays" : c.calendarId}
              </span>
            </span>
            <Switch
              checked={!!c.enabled}
              disabled={toggle.isPending}
              onCheckedChange={(enabled) =>
                toggle.mutate({ calendarId: c.calendarId, calendarName: c.name, enabled })
              }
            />
          </li>
        ))}
      </ul>
      {toggle.isPending && (
        <p className="sticky bottom-0 flex items-center gap-2 bg-background/80 py-2 text-[12px] text-muted-foreground backdrop-blur">
          <Loader2 className="h-3 w-3 animate-spin" /> Resyncing…
        </p>
      )}
    </div>
  );
}
